// 定义 嗅探信息类型
export interface SniffType {
  isMobile: boolean;
  isSafari: boolean;
  isViduApp: boolean;
  isLenovoMini: boolean;
  isWechat: boolean;
  isMacOS: boolean;
  os: 'android' | 'ios' | 'web' | 'lenovo_pc' | 'lenovo_pad';
  appVersion: string;
  codeVersion: string;
  isPWAMode: boolean;
  supportsHover: boolean; // 是否支持hover交互
}

export const initSniff: SniffType = {
  isMobile: false, // 是否是移动设备
  isSafari: false, // 是否是Safari浏览器
  isViduApp: false, // 是否是viduApp
  isLenovoMini: false, // 是否是联想小程序
  isWechat: false, // 是否是微信
  isMacOS: false, // 是否是Mac OS设备
  os: 'web', // 操作系统，默认web，viduApp=['android','ios'] LenovoMini=['lenovo_pc','lenovo_pad']
  appVersion: '', // app版本
  codeVersion: '', // 代码版本
  isPWAMode: false, // 是否是PWA模式
  supportsHover: true, // 是否支持hover交互，默认支持（SSR兼容）
};

function isPWAMode(): boolean {
  if (typeof window === 'undefined') return false;

  // 检查是否在 standalone 模式下运行
  if (window.matchMedia?.('(display-mode: standalone)').matches) {
    return true;
  }

  // 检查 iOS Safari 的 standalone 模式
  if ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone) {
    return true;
  }
  return false;
}

function supportsHover(isMobile: boolean): boolean {
  // 检测设备是否支持 hover 交互
  // 移动端直接返回 false；非移动端（如 iPad）需通过 CSS 媒体查询 (hover: hover) 进一步判断
  return isMobile ? false : window.matchMedia?.('(hover: hover)').matches || false;
}

export const initFromUserAgent = (userAgent: string): SniffType => {
  const normalizedUserAgent = userAgent.toLowerCase();
  // TODO: 示例 & 测试
  // vidu 安卓
  // userAgent =
  //   'Mozilla/5.0 (Linux; Android 14; M2102K1AC Build/UKQ1.231207.002; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/133.0.6943.49 Mobile Safari/537.36 com.vidu.domestic/1.0.0-dev.1'.toLowerCase();
  // vidu ios
  // userAgent = 'Mozilla/5.0 (iPhone; CPU iPhone OS 18_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) com.vidu.domestic/1.0.0.202502111700'.toLowerCase();
  // 联想PC小程序
  // userAgent = 'Mozilla/5.0  Windows  NT  6.2 : Win64:x64)AppleWebKit / 537.36 (KHTML, like Gecko) Chrome/92.0.4515.159 safari/537.36 lenovo_runtime_pc/1.1.0'.toLowerCase();
  // 联想Android小程序
  // userAgent = 'Mozilla/5.0 (Linux; Android 14; TB320FC Build/UKQ1.240510.002; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/126.0.6478.71 Safari/537.36 lenovo_runtime_pad/1.1.0'.toLowerCase();
  // 微信示例
  // userAgent =
  //   'Mozilla/5.0 (Phone; OpenHarmony 5.0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/114.0.0.0 Safari/537.36 ArkWeb/4.1.6.1 Mobile HuaweiBrowser/5.0.4.303 wechatdevtools/1.06.2503290 MicroMessenger/8.0.5 Language/zh_CN webview/17479235231814349 webdebugger port/12219 token/ac8dbc788822fae064de2c64e6cc1adb'.toLowerCase();

  const sniff = { ...initSniff };

  // 移动设备检测
  sniff.isMobile =
    /android|ipad|webos|iphone|blackberry|iemobile|opera mini|openharmony|mobile|harmony|emui|miui|coloros|oppo|vivo|meizu|huawei|honor|oneplus|samsung|xiaomi|realme|redmi/i.test(
      normalizedUserAgent,
    );

  // 操作系统检测
  if (sniff.isMobile) {
    if (/iphone|ipad|ipod|ios/i.test(normalizedUserAgent)) {
      sniff.os = 'ios';
    } else {
      // 安卓设备检测 - 包括国产ROM和定制系统
      sniff.os = 'android';
    }
  }

  // Mac设备检测
  sniff.isMacOS = /macintosh|mac os x/i.test(normalizedUserAgent);

  // 检查是否包含 Safari 标识，但排除其他浏览器
  const hasSafari = normalizedUserAgent.includes('safari');
  const hasChrome = normalizedUserAgent.includes('chrome') || normalizedUserAgent.includes('chromium');
  const hasFirefox = normalizedUserAgent.includes('firefox');
  const hasEdge = normalizedUserAgent.includes('edge') || normalizedUserAgent.includes('edg');
  // 真正的 Safari 应该包含 Safari 但不包含其他浏览器标识
  sniff.isSafari = hasSafari && !hasChrome && !hasFirefox && !hasEdge;
  // PWA模式检测
  sniff.isPWAMode = isPWAMode();
  // hover支持检测
  sniff.supportsHover = supportsHover(sniff.isMobile);

  // vidu 设备检测
  const viduRegex = /com\.vidu\.(domestic|oversea)\/(\d+\.\d+\.\d+(-\S*)?)\.(\d+)/;
  const viduMatch = normalizedUserAgent.match(viduRegex);
  // 联想设备检测
  const lenovoRegex = /lenovo_runtime_(pc|pad)\/(\d+\.\d+\.\d+)/i;
  const lenovoMatch = normalizedUserAgent.match(lenovoRegex);
  // 微信环境检测
  const wechatMatch = normalizedUserAgent.includes('micromessenger');

  // vidu设备
  if (viduMatch) {
    sniff.isViduApp = true;
    sniff.appVersion = viduMatch[2];
    sniff.codeVersion = viduMatch[4];
  } else if (lenovoMatch) {
    // 联想设备
    sniff.isLenovoMini = true;
    sniff.os = lenovoMatch[1] === 'pc' ? 'lenovo_pc' : 'lenovo_pad';
    sniff.appVersion = lenovoMatch[2];
  } else if (wechatMatch) {
    // 微信环境
    sniff.isWechat = true;
  }
  return sniff;
};
